import { dungApiThat, goiApi } from '../api/http';
import { mayChuModelAiNenTang } from '../../mock/ai-model';
import { mayChuGoiNapToken } from '../../mock/goi-nap-token';
import { mayChuModelAi } from '../../mock/ocr';
import type {
  AiModel,
  AiModelCreate,
  AiModelMultiplierSuggestionList,
  AiModelOption,
  AiModelUpdate,
  TenantAiModel,
} from '../../modules/ai-model/types';
import { GOC_OCR, giaLap, giaLapDanhSach, kiemTrang, maHoa } from './ocr-chung';

const GOC = '/api/v1/ai-models';
const theoTenant = (tenantId: string) => `/api/v1/tenants/${maHoa(tenantId)}/ai-models`;

/**
 * Danh mục model AI cấp nền tảng (ADR-0083, tenant-service, nhóm route nền tảng — chỉ token
 * `PLATFORM_ADMIN`). Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/ai-model.ts`), nên
 * `queryKey` và việc vô hiệu hoá cache hành xử y hệt ở cả hai chế độ.
 *
 * Khoá nhà cung cấp AI **không** đi qua đây: vẫn ở biến môi trường của module (ADR-0066).
 */
export const aiModelRepository = {
  /** `GET /api/v1/ai-models` — mọi model kể cả đã tắt, theo `display_order` rồi tên. Không phân trang. */
  danhSach: (): Promise<AiModel[]> =>
    dungApiThat()
      ? goiApi<{ items: AiModel[] }>(GOC).then((t) => kiemTrang(t).items)
      : giaLapDanhSach(() => mayChuModelAiNenTang.danhSach().items, []),

  /** `POST /api/v1/ai-models` → `201`. `key` server tự sinh; không sinh được khoá không trùng: `409 AI_MODEL_KEY_EXISTS`, `#/label`. */
  tao: (du: AiModelCreate, khoaLap: string): Promise<AiModel> =>
    dungApiThat()
      ? goiApi<AiModel>(GOC, { method: 'POST', body: du, idempotencyKey: khoaLap })
      : giaLap(() => mayChuModelAiNenTang.tao(du)),

  /** `PATCH /api/v1/ai-models/{id}` — chỉ trường đổi. Tắt model đang mặc định: `400`, `#/is_active`. */
  sua: (id: string, du: AiModelUpdate): Promise<AiModel> =>
    dungApiThat()
      ? goiApi<AiModel>(`${GOC}/${maHoa(id)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuModelAiNenTang.sua(id, du)),

  /**
   * `GET /api/v1/ai-models/multiplier-suggestions` — hệ số token Fox gợi ý từ Bảng giá model (ADR-0100 mục 2). Chỉ đọc,
   * lưu hệ số bằng `sua`.
   */
  goiYHeSo: (): Promise<AiModelMultiplierSuggestionList> =>
    dungApiThat()
      ? goiApi<AiModelMultiplierSuggestionList>(`${GOC}/multiplier-suggestions`)
      : giaLap(() => mayChuModelAiNenTang.goiYHeSo(mayChuGoiNapToken.donGia())),

  /** `GET /api/v1/tenants/{id}/ai-models` — model đang bật của danh mục, `enabled` theo tenant. */
  theoTenant: (tenantId: string): Promise<TenantAiModel[]> =>
    dungApiThat()
      ? goiApi<{ items: TenantAiModel[] }>(theoTenant(tenantId)).then((t) => kiemTrang(t).items)
      : giaLapDanhSach(() => mayChuModelAiNenTang.theoTenant(tenantId).items, []),

  /** `PUT /api/v1/tenants/{id}/ai-models` — thay **cả tập** model bị tắt cho tenant. */
  datTatChoTenant: (tenantId: string, disabledKeys: string[]): Promise<TenantAiModel[]> =>
    dungApiThat()
      ? goiApi<{ items: TenantAiModel[] }>(theoTenant(tenantId), {
          method: 'PUT',
          body: { disabled_keys: disabledKeys },
        }).then((t) => kiemTrang(t).items)
      : giaLap(() => mayChuModelAiNenTang.datTat(tenantId, disabledKeys).items),

  /**
   * `GET /api/v1/m/ocr/ai-models/options` của **module ocr** (cổng tenant): model tenant dùng được,
   * kèm vai làm được — ô chọn model ở cấu hình loại chứng từ, nhận dạng, kho bản OCR.
   */
  luaChon: (): Promise<AiModelOption[]> =>
    dungApiThat()
      ? goiApi<{ items: AiModelOption[] }>(`${GOC_OCR}/ai-models/options`).then((t) => kiemTrang(t).items)
      : giaLapDanhSach(() => mayChuModelAi.luaChon().items, []),
};
